import { formatLongSailingType } from "@/lib/formatters/SailingStatusCodeFormatter";
import { FormModelChangeHandler } from "@/types/forms/types";
import { PriceCalculatorFormData } from "@/features/price-calculator/types/types";
import { PriceCalcSailingsFragment, SailingTypeCode } from "@/gql/graphql";
import { Select, SelectProps } from "@stenajs-webui/select";
import * as React from "react";
import { useMemo } from "react";
import { SingleValue } from "react-select";

const formatTimeWithColon = (time: string) =>
  `${time.slice(0, 2)}:${time.slice(2)}`;

const transformSailingDeparturesToOptions = (
  sailings?: PriceCalcSailingsFragment[]
) =>
  sailings?.map((sailing) => ({
    label:
      formatTimeWithColon(sailing.departureTime) +
      " - " +
      formatLongSailingType(sailing.sailingTypeCode as SailingTypeCode),
    value: `${sailing.sailingTypeCode}_${sailing.departureTime}`,
    type: "departureTime",
    departureTime: sailing.departureTime,
  })) ?? [];

export interface SailingCategoryOption {
  label?: string;
  value?: string;
  type?: string;
  departureTime?: string;
}

interface Props extends Omit<SelectProps<any>, "value" | "onChange"> {
  value?: string;
  onValueChange?: (value: SailingCategoryOption) => void;
  sailings?: PriceCalcSailingsFragment[];
  loading?: boolean;
  departureTime?: string;
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>;
}
export const enterManuallyDefaultOption: SailingCategoryOption = {
  label: "Enter manually",
  value: "Manual",
  type: "sailingCategory",
  departureTime: "11:11",
};

export const SailingCategoryTimeTableSelect: React.FC<Props> = ({
  onValueChange,
  value,
  sailings,
  loading,
  departureTime,
  onChangeFormData,
  ...selectProps
}) => {
  const sailingCategories = useMemo(() => {
    return [
      enterManuallyDefaultOption,
      ...transformSailingDeparturesToOptions(sailings),
    ];
  }, [sailings]);

  const selected = useMemo(() => {
    if (value === enterManuallyDefaultOption.value) {
      return enterManuallyDefaultOption;
    }

    return sailingCategories.find(
      (option) => option.value === `${value}_${departureTime}`
    );
  }, [sailingCategories, value, departureTime]);

  return (
    <Select
      {...selectProps}
      value={selected}
      options={sailingCategories}
      isLoading={loading}
      menuPortalTarget={document.body}
      onChange={(item: SingleValue<SailingCategoryOption>) => {
        if (item) {
          const sailingTypeCode =
            item.value === enterManuallyDefaultOption.value
              ? item.value
              : item.value?.split("_")[0];
          onValueChange &&
            onValueChange({
              ...item,
              value: sailingTypeCode,
            });
        }
      }}
    />
  );
};
